import styled from "@emotion/styled";
import React from "react";
import UI from "@mile-meeting/mile-ui";
import { TMeetingRoom } from "apis/meetings/meeting-room";
import GoogleMeetingRoomNoticePopover from "./MinimumAttendeesSetting/GoogleMeetingRoomNoticePopover/GoogleMeetingRoomNoticePopover";

const MeetingRoomInfo = ({ room }: { room?: TMeetingRoom }) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const [openPopover, setOpenPopover] = React.useState<boolean>(false);
  const renderBuildingFloor = () => {
    if (room?.building?.name && room?.floor?.name) {
      return `${room?.building.name} ${room?.floor.name}`;
    }
    if (!room?.building?.name && room?.floor?.name) {
      return `건물 미설정 / ${room?.floor.name}`;
    }
    if (room?.building?.name && !room?.floor?.name) {
      return `${room?.building.name} / 층 미설정`;
    }
    return "건물/층 미설정";
  };

  return (
    <Container>
      <ImageContainer image={room?.image}>
        {!room?.image && <UI.Icon name="image" size={24} color="var(--alias-color-label-assistive)" />}
      </ImageContainer>
      <InfoContainer>
        <TitleContainer>
          <div ref={ref}>
            <UI.Typography
              type="body01 normal"
              fontStyle="bold"
              color="var(--alias-color-label-normal)"
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {room?.name}
            </UI.Typography>
          </div>
          {room?.meetingRoomIntegration?.integrationProvider === "GOOGLE" && (
            <GoogleContainer>
              <UI.Icon name="google" size={20} />
              <div onClick={() => setOpenPopover(true)}>
                <UI.Typography
                  type="label01"
                  color="var(--alias-color-label-alternative)"
                  style={{ textDecoration: "underline", cursor: "pointer" }}
                >
                  유의사항
                </UI.Typography>
              </div>
            </GoogleContainer>
          )}
          <GoogleMeetingRoomNoticePopover
            onClose={() => {
              setOpenPopover(false);
            }}
            modalIsOpen={openPopover}
            modalPosition={{
              top: 26,
              left:
                368 - (ref.current?.getClientRects()[0].width ?? 0) - 28 < 285
                  ? 83
                  : 368 - (ref.current?.getClientRects()[0].width ?? 0) - 28,
            }}
          />
        </TitleContainer>
        <ContentContainer>
          <InfoSection>
            <div style={{ display: "flex", gap: 4, width: "100%", alignItems: "center" }}>
              <UI.Icon name="office" size={16} color="var(--alias-color-label-assistive)" />
              <UI.Typography type="label01" fontStyle="bold" color="var(--alias-color-label-alternative)">
                위치
              </UI.Typography>
            </div>
            <UI.Typography
              type="label01"
              fontStyle="regular"
              color="var(--alias-color-label-normal)"
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {renderBuildingFloor()}
            </UI.Typography>
          </InfoSection>
          <InfoSection>
            <div style={{ display: "flex", gap: 4, width: "100%", alignItems: "center" }}>
              <UI.Icon name="people" size={16} color="var(--alias-color-label-assistive)" />
              <UI.Typography type="label01" fontStyle="bold" color="var(--alias-color-label-alternative)">
                수용 인원
              </UI.Typography>
            </div>
            <UI.Typography type="label01" fontStyle="regular" color="var(--alias-color-label-normal)">
              {room?.capacity}인
            </UI.Typography>
          </InfoSection>
        </ContentContainer>
      </InfoContainer>
    </Container>
  );
};

export default React.memo(MeetingRoomInfo);

const Container = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  align-self: stretch;
  width: 100%;
  height: max-content;
`;

const ImageContainer = styled.div<{ image?: string }>`
  display: flex;
  width: 136px;
  height: 136px;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;

  border-radius: 4px;
  background: ${(props) => (props.image ? "white" : "var(--component-color-fill-alternative)")};
  background-image: url(${(props) => props.image});
  background-position: center;
  background-size: cover;
  background-repeat: no-repeat;
`;

const InfoContainer = styled.div`
  width: calc(100% - 176px);
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
`;

const TitleContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 4px;
  align-items: center;
  align-self: stretch;
  position: relative;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-self: stretch;
`;

const InfoSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  align-self: stretch;
`;

const GoogleContainer = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
  position: relative;
`;
